import axios from "axios";
import compact from "lodash/compact";
import join from "lodash/join";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { Unit } from "../../types/customization/box";
import { SelectionAlbum, SelectionPhoto } from "../../types/selection/selection";
import { THUMBNAIL_FIT_SIZE } from "../../utils/selection/photo";
import BaseSelectionProvider from "./BaseSelectionProvider";

const VERSION = "v3.3";

export default class FacebookSelectionProvider extends BaseSelectionProvider {
  accessToken: string;
  albums: {
    data: SelectionAlbum[];
    paging?: string;
  };
  albumPhotos: {
    [albumRef: string]: {
      data: SelectionPhoto[];
      paging?: string;
    };
  };

  constructor(accessToken: string) {
    super();
    this.accessToken = accessToken;
    this.albums = { data: [] };
    this.albumPhotos = {};
  }

  hasAlbums(): boolean {
    return true;
  }
  hasMoreAlbums(): boolean {
    return !!this.albums.paging;
  }
  hasMoreAlbumPhotos(albumRef: string): boolean {
    return !!this.albumPhotos[albumRef] && !!this.albumPhotos[albumRef].paging;
  }

  refreshAlbums(): void {
    this.albums = { data: [] };
    return;
  }
  refreshAlbumPhotos(): void {
    this.albumPhotos = {};
    return;
  }

  async getAlbums(): Promise<SelectionAlbum[]> {
    if (this.albums.data.length > 0) {
      return this.albums.data;
    }
    this.albums.data = await this.getAlbumsDistant();
    return this.albums.data;
  }

  async getMoreAlbums(): Promise<SelectionAlbum[]> {
    if (!this.hasMoreAlbums()) {
      return this.albums.data;
    }
    const nextAlbums = await this.getAlbumsDistant(true);
    this.albums.data = this.albums.data.concat(nextAlbums);
    return this.albums.data;
  }

  async getAlbumsDistant(next = false): Promise<SelectionAlbum[]> {
    const params: GetParams = {
      limit: 50,
    };
    if (next && this.hasMoreAlbums()) {
      params.after = this.albums.paging;
    }
    const { data, paging } = await this.get<AlbumResponse>({
      edge: "me/albums",
      fields: ["id", "count", "cover_photo", "name"],
      params,
    });
    this.albums.paging = paging.next ? paging.cursors.after : undefined;

    return compact(
      map(data, (datum) => {
        if (!datum.cover_photo || datum.count === 0) {
          return;
        }

        return {
          coverPictureUrl: `https://graph.facebook.com/${datum.cover_photo.id}/picture?access_token=${this.accessToken}&type=normal`,
          name: datum.name,
          ref: datum.id,
        };
      })
    );
  }

  async getAlbumPhotos(albumRef: string): Promise<SelectionPhoto[]> {
    if (this.albumPhotos[albumRef] && this.albumPhotos[albumRef].data.length > 0) {
      return this.albumPhotos[albumRef].data;
    }
    this.albumPhotos[albumRef] = { data: [] };
    this.albumPhotos[albumRef].data = await this.getAlbumPhotosDistant(albumRef);
    return this.albumPhotos[albumRef].data;
  }

  async getMoreAlbumPhotos(albumRef: string): Promise<SelectionPhoto[]> {
    if (!this.hasMoreAlbumPhotos(albumRef)) {
      return this.albumPhotos[albumRef].data;
    }
    const nextAlbums = await this.getAlbumPhotosDistant(albumRef, true);
    this.albumPhotos[albumRef].data = this.albumPhotos[albumRef].data.concat(nextAlbums);
    return this.albumPhotos[albumRef].data;
  }

  async getAlbumPhotosDistant(albumRef: string, next = false): Promise<SelectionPhoto[]> {
    const params: GetParams = {
      limit: 50,
    };
    if (next && this.hasMoreAlbumPhotos(albumRef)) {
      params.after = this.albumPhotos[albumRef].paging;
    }
    const { data, paging } = await this.get<AlbumPhotoResponse>({
      edge: `${albumRef}/photos`,
      fields: ["id", "images"],
      params,
    });
    this.albumPhotos[albumRef].paging = paging.next ? paging.cursors.after : undefined;

    return map(data, (datum) => {
      const largest = findLargest(datum.images);

      return {
        key: `facebook-${datum.id}`,

        height: { unit: Unit.PX, value: largest.height } as const,
        width: { unit: Unit.PX, value: largest.width } as const,

        provider: "facebook",
        providerRef: datum.id,

        providerPictureUrl: largest.source,
        providerThumbnailUrl: findThumbnail(datum.images).source,
      };
    });
  }

  async get<R>({
    edge,
    fields,
    params,
  }: {
    edge: string;
    fields: string[];
    params: GetParams;
  }): Promise<R> {
    const url = `https://graph.facebook.com/${VERSION}/${edge}`;
    const response = await axios.get<R>(url, {
      params: {
        ...params,
        access_token: this.accessToken,
        fields: join(fields, ","),
      },
    });

    return response.data;
  }
}

interface AlbumResponse {
  data: Array<{
    count: number;
    cover_photo?: {
      id: string;
    };
    id: string;
    name: string;
  }>;
  paging: Paging;
}

interface AlbumPhotoResponseImage {
  height: number;
  source: string;
  width: number;
}

interface AlbumPhotoResponse {
  data: Array<{
    id: string;
    images: AlbumPhotoResponseImage[];
  }>;
  paging: Paging;
}

interface Paging {
  cursors: {
    after: string;
  };
  next?: string;
}

interface GetParams {
  limit: number;
  after?: string;
}

function findLargest(images: AlbumPhotoResponseImage[]): AlbumPhotoResponseImage {
  const sortedImages = orderBy(images, (image) => image.width, "desc");
  return sortedImages[0];
}

function findThumbnail(images: AlbumPhotoResponseImage[]): AlbumPhotoResponseImage {
  const sortedImages = orderBy(images, (image) => image.width, "asc");

  for (const image of sortedImages) {
    if (image.height >= THUMBNAIL_FIT_SIZE && image.width >= THUMBNAIL_FIT_SIZE) {
      return image;
    }
  }

  return images[0];
}
